import React from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { Tag, UserRoundCheck } from 'lucide-react'

import { font, space } from 'settings/tokens'

import { SheetChoiceRow } from './SheetChoiceRow'

/**
 * VERA-637 — what "Sell" means, asked before anything else.
 *
 * Two genuinely different things had been living behind one button. `SellCertificate` sends an
 * offer to ONE named person — the seller already knows who is buying. A LISTING names nobody: it
 * publishes an asking price on the certificate's public page so whoever opens the link can buy.
 * A seller who wants the second has, until now, had no way to express it, and a seller who wants
 * the first was never told the other existed.
 *
 * ## A STEP of the sell sheet, not another drawer (§52)
 *
 * Two levels of bottom sheet is the maximum, and the sell flow already spends its second level on
 * the confirm scan. So this swaps the level-one sheet's BODY, exactly as the sale-mode step does.
 * A third sheet here would have put the chooser under two scrims with three dismissal controls on
 * screen at once.
 */

export type SellMethod = 'list' | 'offer'

interface SellMethodSelectProps {
  onSelect: (method: SellMethod) => void
  /**
   * VERA-637 — whether this certificate already carries an asking price, which changes the
   * listing row from an offer to make one into a way to change or remove it. Naming the current
   * state matters: without it a listed item looks unlisted, and the obvious move is to list it
   * again at a price the owner cannot see.
   */
  isListed?: boolean
}

export const SellMethodSelect = ({ onSelect, isListed = false }: SellMethodSelectProps) => {
  const theme = useTheme()

  /*
    VERA-632 review — the "Do you know who is buying?" lead-in is GONE.

    It was a question the two rows beneath already answer: one says "anyone who opens this
    certificate", the other says "someone in particular". Asking it first made the reader
    parse a question, hold it, then map two answers onto it, when the rows are directly
    readable on their own. It also pushed the first row down the sheet for no information.

    The rows are `spacing={0}` because `ListRow`'s hairline IS the separator — a gap between
    them would leave each divider floating in the middle of a space rather than dividing two
    rows (that component's own contract).
  */
  return (
    <Stack direction='column' spacing={0} sx={{ paddingBottom: `${space.lg}px` }}>
      <SheetChoiceRow
        icon={<Tag size={20} strokeWidth={1.5} color={theme.palette.text.primary} />}
        // "Edit listing", matching the certificate page's own sale card verbatim (§44 — one
        // term, every surface reads it). The row used to read "Change your listing to sell",
        // which said the same thing in seven words and in a different verb from the card that
        // opens this very step. The context that longer phrasing was carrying is not lost:
        // the sheet is titled Sell, the sibling row sends an offer, and the description below
        // says what a listing is.
        title={isListed ? 'Edit listing' : 'List for sale'}
        description={
          isListed
            ? // Names the step's two controls in the words they actually carry — "Update
              // price" and "Remove listing". It previously said "no longer list this for
              // sale", which described the outcome in a phrase matching neither button.
              'Update your asking price, or remove the listing.'
            : // UNBRANCHED, and that is a decision rather than an oversight. Every other surface
              // stating who can see the price now branches on the visibility switch
              // (`saleListingAudienceCopy`); this one cannot, and must not.
              //
              // It renders ONLY in the not-listed state, where there is no listing whose
              // audience could be described — it is describing what listing WOULD do. What it
              // would do is governed by the listing step's default, which is ON (see
              // `visibilityChoice` in `ListForSale`), so this sentence is true as written.
              //
              // Branching it on the stored `show_sale_listing` would introduce a NEW falsehood:
              // an unlisted certificate whose view happens to carry a stored `false` would be
              // told its listing will be private, and then the listing step would default it
              // public. ⚠️ If that default ever changes, this sentence changes with it.
              'Set an asking price. Anyone who opens this certificate can buy it.'
        }
        onClick={() => onSelect('list')}
        divider
      />

      <SheetChoiceRow
        icon={<UserRoundCheck size={20} strokeWidth={1.5} color={theme.palette.text.primary} />}
        // Names the mechanism, because this row is also reached from the card kebab, where
        // there is no sheet title or sibling row to supply the context "offer" needs.
        //
        // "Sell direct", not "Sell peer to peer". The old label named the ARCHITECTURE, not
        // the choice: "peer to peer" is a term a seller has to already know to parse, and it
        // says nothing the sibling row ("anyone who opens this certificate") does not already
        // contrast with. "Direct" is the ordinary English for the distinction being drawn —
        // straight to one named person rather than out to a public listing.
        title='Sell direct'
        description='Sell to someone in particular. They are notified and pay you directly.'
        onClick={() => onSelect('offer')}
        divider={false}
      />
    </Stack>
  )
}
